/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import Heading from '@tds/core-heading';
import PropTypes from 'prop-types';
import { useFormik, Formik } from 'formik';
import PopupModal from '@tds/community-modal';
import * as Yup from 'yup';

// import OrderSummary from '../form/orderSummary';
import Axios from 'axios';
import { Download } from '@tds/core-interactive-icon';
import { get } from 'lodash';
import Modal from '../../design/shared/modal';
import { Box } from '../../design/css';
import { customStyles } from '../css';

import OrderButton from '../../salesRepDashboard/Actions/components/orderButton';
import Comments from './comments';
import { FieldInput, FieldMultiLineText } from '../../form/fields';
import request, { apiHost, config } from '../../utils/request';
import { calculateFinancials, extractNumber, getTcvDmTermThreshold } from '../../form/fmUtils/utils';
import FieldFile from '../../form/fieldFile';
import { AttachmentContainer, FieldTitle } from './css';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { formatCurrency } from '../../utils/currencyFormatter';

const getFormattedCurrencyValue = (currency) => {
  return formatCurrency(parseFloat(currency || 0).toFixed(2));
};

// New code starts here
const validationSchema = Yup.object().shape({
  discountValue: Yup.number()
    .transform((value, originalValue) =>
      originalValue.trim() === '' ? null : value
    )
    .nullable()
    .moreThan(0, 'Discount value must be greater than 0')
    .max(
      'dueMonthly',
      `Discount value must be less than or equal to Due Monthly`
    ),
  oneTimeDiscountValue: Yup.number()
    .transform((value, originalValue) =>
      originalValue.trim() === '' ? null : value
    )
    .nullable()
    .moreThan(0, 'One time discount value must be greater than 0')
    .max(
      'OneTimeCharges',
      `One time discount value must be less than or equal to Due Monthly`
    ),
  discountApprovalId: Yup.string().when(
    ['discountValue', 'oneTimeDiscountValue'],
    {
      is: (discountValue, oneTimeDiscountValue) =>
        discountValue > 0 || oneTimeDiscountValue > 0,
      then: Yup.string().required(
        'Approver is required when either discount value or one time discount value is greater than 0'
      ),
      otherwise: Yup.string().notRequired()
    }
  )

});

const initialValuesDefault = {
  discountApprovalId: '',
  status: '',
  dateSubmitted: '',
  dateAmended: '',
  reasonForDiscount: '',
  attachment: '',
  companyName: '',
  contractTerm: '',
  managedITServicesTotal: '',
  onboardingFee: '',
  requestedMonthlyDiscount: '',
  requestedOneTimeDiscount: '',
  tcvDMPercent: '',
  trueMonthlyCost: '',
  compensation: '',
  professionalServicesCost: '',
  action: '',
  comment: '',
  submitAttachment: '',
  monthlyDiscount: 0,
  oneTimeDiscount: 0, 
};

const DiscountCalculatorDetails = ({ discountData, open, setOpen, onStatusUpdateSuccess, isViewOnly }) => {
  const [discount, setDiscount] = useState({});
  const [loading, setLoading] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const orderChannel = get(discount, 'order.channel', '')

  const localizedStrings = useLocalizedStringsContext();
  const {
    srtDashboard: { confirmationDialog },
    formButtons: { confirmButtonLabel, cancelButtonLabel }
  } = localizedStrings;

  const updateDiscountData = async(body) => {
    const data = await request.put(
      `/discounts/${discountData.id}`,
      body
    );
    return data.data;
  };

  const formik = useFormik({
    initialValues: initialValuesDefault,
    onSubmit: async (values, actions) => {
      setIsConfirmationModalOpen(false);
      try {
        await validationSchema.validate(values, { abortEarly: false });
        const updatePayload = {};
        if(values.action === 'Approve'){
          updatePayload.status = 'Approved';
          updatePayload.approvedOnetime = +values.oneTimeDiscount;
          updatePayload.approvedMonthly = +values.monthlyDiscount;
        } else if(values.action === 'Reject') {
          updatePayload.status = 'Rejected';
        }
        if(values.commentFromApprover) {
          updatePayload.commentFromApprover = values.commentFromApprover;
        }
        setLoading(true);
        await updateDiscountData(updatePayload);
        if(values.attachment){
          await uploadFile(values.attachment, discount.id);
        }
        setLoading(false);
        onStatusUpdateSuccess();
      } catch (validationErrors) {
        const errors = {};
        validationErrors.inner.forEach(error => {
          errors[error.path] = error.message;
        });
        actions.setErrors(errors);
      } finally {
        actions.setSubmitting(false);
      }
    }
    // validationSchema
  });

  const fetchDiscountData = async() => {
    const data = await request.get(
      `/discounts/${discountData.id}`
    );
    return data.data;
  };

  const getManagedItServicesTotal = (order) => {
    let additionalServicesTotal = 0;
    if(order.additionalServices) {
      const additionalServicesArray = JSON.parse(order.additionalServices);
      const secondItem = additionalServicesArray[1];
      if(secondItem) additionalServicesTotal = secondItem;
    }
    return parseFloat(order.managedITServicesTotal || 0) - additionalServicesTotal;
  };

  const setCalculationFields = (
    oneTimeDiscount,
    monthlyDiscount,
    discountItem,
    existingValues,
    trueMonthlyCost = null, // Add these parameters
    compensation = null,
    professionalServicesCost = null,
    channel
  ) => {
    const { term, order = {} } = discountItem;
    const managedITServicesTotal = getManagedItServicesTotal(order);
    const {
      MRR,
      trueMonthlyCost: calculatedTrueMonthlyCost,
      grossMargin,
      grossMarginPercentage,
      compensation: calculatedCompensation,
      professionalServicesOnboarding,
      professionalServicesCost: calculatedProfessionalServicesCost,
      professionalServicesMargin,
      professionalServicesMarginPercentage,
      TCV_DM,
      TCV_DM_Percentage,
    } = calculateFinancials(
      extractNumber(term),
      parseFloat(managedITServicesTotal || 0),
      parseFloat(order.onboardingFee || 0),
      monthlyDiscount,
      oneTimeDiscount,
      trueMonthlyCost, 
      compensation,
      professionalServicesCost,
      channel
    );

    const requestedTcvDMPercentValue = calculateFinancials(
      extractNumber(term),
      parseFloat(managedITServicesTotal || 0),
      parseFloat(order.onboardingFee || 0),
      discountData.monthly,
      discountData.onetime,
      undefined,
      undefined,
      undefined,
      channel
    ).TCV_DM_Percentage
  
    formik.setValues({
      ...existingValues,
      tcvDMPercent: `${(TCV_DM_Percentage || 0).toFixed(2)}%`,
      totalContractTCV: getFormattedCurrencyValue(TCV_DM),
      professionalServicesMarginPercentage: `${(
        professionalServicesMarginPercentage || 0
      ).toFixed(2)}%`,
      professionalServicesMargin: getFormattedCurrencyValue(professionalServicesMargin),
      professionalServicesCost: +calculatedProfessionalServicesCost.toFixed(2),
      professionalServicesOnboarding: getFormattedCurrencyValue(professionalServicesOnboarding),
      compensation: +calculatedCompensation.toFixed(2),
      grossMarginPercentage: `${(grossMarginPercentage || 0).toFixed(2)}%`,
      grossMargin: getFormattedCurrencyValue(grossMargin),
      trueMonthlyCost: +calculatedTrueMonthlyCost.toFixed(2),
      monthlyRecurringRevenue: getFormattedCurrencyValue(MRR),
      requestedTcvDMPercent: `${(requestedTcvDMPercentValue || 0).toFixed(2)}%`
    });
  };

  const setDefaultValues = (discountItem, channel) => {
    const {
      id,
      term,
      onetime,
      monthly,
      approvedOnetime,
      approvedMonthly,
      status,
      comment,
      createdAt,
      updatedAt,
      commentFromApprover,
      order = {},
    } = discountItem;
    const managedITServicesTotal = getManagedItServicesTotal(order);
    const existingValues = {
      discountApprovalId: id,
      status,
      dateSubmitted: new Date(createdAt).toLocaleDateString('en-GB'),
      dateAmended: new Date(updatedAt).toLocaleDateString('en-GB'),
      reasonForDiscount: comment,
      attachment: '',
      companyName: order.companyName,
      contractTerm: extractNumber(term),
      managedITServicesTotal: getFormattedCurrencyValue(managedITServicesTotal),
      onboardingFee:  getFormattedCurrencyValue(order.onboardingFee),
      requestedMonthlyDiscount: getFormattedCurrencyValue(monthly),
      requestedOneTimeDiscount: getFormattedCurrencyValue(onetime),
      monthlyDiscount: +approvedMonthly || +monthly,
      oneTimeDiscount: +approvedOnetime || +onetime,
      commentFromApprover
    }
    setCalculationFields(approvedOnetime || onetime, approvedMonthly || monthly, discountItem, existingValues, null, null, null, channel);
  }

  useEffect(() => {
    const fetchData = async () => {
      const currentDiscountData = await fetchDiscountData();
      if (currentDiscountData) {
        setDiscount(currentDiscountData);
        setDefaultValues(currentDiscountData, get(currentDiscountData, 'order.channel', ''));
      }
    };
    fetchData();
  }, []);

  const onFileDownload = (filename, id) => {
    // setExportingDefects(true);
    const fileExtension = filename
      .split('.')
      .pop()
      .toLowerCase();

    const url = `${apiHost}/discounts/${discount.id}/attachment/${id}`;
    Axios
      .get(url, { ...config(), responseType: `application/${fileExtension}` })
      .then(response => {
        const data = Uint8Array.from(response.data.content.data);
        const blob = new Blob([data.buffer], {
          type: `application/${fileExtension}`
        });
        if (fileExtension === 'pdf') {
          const fileURL = URL.createObjectURL(blob);
          window.open(fileURL);
        } else {
          // Create a download link
          const downloadLink = document.createElement('a');
          downloadLink.href = URL.createObjectURL(blob);

          // Set the download attribute with a suggested file name
          downloadLink.download = filename;

          // Append the link to the body and programmatically trigger the click event
          document.body.appendChild(downloadLink);
          downloadLink.click();

          // Remove the link from the DOM
          document.body.removeChild(downloadLink);
        }
      })
      .catch(() => {});
  };

  const uploadFile = (file, id) => {
    const formData = new FormData();
    formData.append('file', file);
    return request.post(`/discounts/${id}/attachment`, formData);
  };

  const shouldNeedConfirmation = () => {
    return (+(formik.values.tcvDMPercent.replace('%','')) < getTcvDmTermThreshold(discount.term, orderChannel)) &&
      formik.values.action === 'Approve';
  };

  return (
    <div id="modal">
      <Modal
        open={open}
        setOpen={setOpen}
        customStyles={customStyles}
        header='Discount Calculator'
      >
        <Formik initialValues={formik.initialValues}>
          <form>
            {/* Discount Approval Details */}
            <div>
              <Heading level="h3">Discount Approval Details</Heading>
              <FieldInput
                data={{
                  ref: null,
                  name: 'discountApprovalId',
                  type: 'Number',
                  label: 'Discount Approval ID',
                  value: formik.values.discountApprovalId,
                  required: true,
                  onChange: e =>
                    formik.setFieldValue('discountApprovalId', e.target.value),
                  readonly: true,
                  note: null,
                  disabled: true,
                  noteAppearsBelowField: false,
                  isShowNoteBackground: false,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'status',
                  type: 'String',
                  label: 'Status',
                  value: formik.values.status,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'dateSubmitted',
                  type: 'String',
                  label: 'Date Submitted',
                  value: formik.values.dateSubmitted,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'dateAmended',
                  type: 'String',
                  label: 'Date Amended',
                  value: formik.values.dateAmended,
                  required: false,
                  onChange: e =>
                    formik.setFieldValue('dateAmended', e.target.value),
                  readonly: false,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'reasonForDiscount',
                  type: 'String',
                  label: 'Reason for Discount',
                  value: formik.values.reasonForDiscount,
                  required: true,
                  onChange: e =>
                    formik.setFieldValue('reasonForDiscount', e.target.value),
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              {/* <Attachements orderData={{}} /> */}
            </div>

            {/* Proposal Details */}
            <div>
              <Heading level="h3">Proposal Details</Heading>
              <FieldInput
                data={{
                  ref: null,
                  name: 'companyName',
                  type: 'String',
                  label: 'Company Name',
                  value: formik.values.companyName,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'contractTerm',
                  type: 'Number',
                  label: 'Contract Term (months)',
                  value: formik.values.contractTerm,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'managedITServicesTotal',
                  type: 'Currency',
                  label: 'Managed IT Services Total',
                  value: formik.values.managedITServicesTotal,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'onboardingFee',
                  type: 'Currency',
                  label: 'Onboarding Fee',
                  value: formik.values.onboardingFee,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
            </div>

            {/* Requested Discount */}
            <div>
              <Heading level="h3">Requested Discount</Heading>
              <FieldInput
                data={{
                  ref: null,
                  name: 'requestedMonthlyDiscount',
                  type: 'Currency',
                  label: 'Requested Monthly Discount',
                  value: formik.values.requestedMonthlyDiscount,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true,
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'requestedOneTimeDiscount',
                  type: 'Currency',
                  label: 'Requested One Time Discount',
                  value: formik.values.requestedOneTimeDiscount,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'requestedTcvDMPercent',
                  type: 'Percentage',
                  label: 'TCV DM%',
                  value: formik.values.requestedTcvDMPercent,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
            </div>
            <div>
              <Heading level="h3">Total Contract</Heading>
              <FieldInput
                data={{
                  ref: null,
                  name: 'totalContractTCV',
                  type: 'Currency',
                  label: 'TCV DM',
                  value: formik.values.totalContractTCV,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'tcvDMPercent',
                  type: 'Percentage',
                  label: 'TCV DM%',
                  value: formik.values.tcvDMPercent,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
            </div>

            {/* Discount Calculator */}
            <div>
              <Heading level="h3">Discount Calculator</Heading>
              <FieldInput
                data={{
                  ref: null,
                  name: 'monthlyDiscount',
                  type: 'Number',
                  label: 'Monthly Discount',
                  value: formik.values.monthlyDiscount,
                  required: false,
                  onChange: e => {
                    setCalculationFields(formik.values.oneTimeDiscount || 0, +e.target.value || 0, discount, formik.values, null, null, null, orderChannel);
                    formik.setFieldValue('monthlyDiscount', e.target.value);
                  },
                  readonly: false,
                  note: null,
                  disabled: isViewOnly,
                  horizontalFormat: true,
                  labelColor: '#2b8000'
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'oneTimeDiscount',
                  type: 'Number',
                  label: 'One Time Discount',
                  value: formik.values.oneTimeDiscount,
                  required: false,
                  onChange: (e) => {
                    setCalculationFields(+e.target.value || 0, formik.values.monthlyDiscount || 0, discount, formik.values, null, null, null, orderChannel);
                    formik.setFieldValue('oneTimeDiscount', e.target.value)
                  },
                  readonly: false,
                  note: null,
                  disabled: isViewOnly,
                  horizontalFormat: true,
                  labelColor: '#2b8000'
                }}
              />
            </div>
            <div>
              <Heading level="h3">Monthly Recurring Revenue</Heading>
              <FieldInput
                data={{
                  ref: null,
                  name: 'monthlyRecurringRevenue',
                  type: 'Currency',
                  label: 'MRR',
                  value: formik.values.monthlyRecurringRevenue,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'trueMonthlyCost',
                  type: 'Currency',
                  label: 'True Monthly Cost',
                  value: formik.values.trueMonthlyCost,
                  required: false,
                  onChange: (e) => {
                    const newValue = parseFloat(e.target.value) || 0;
                    setCalculationFields(
                      formik.values.oneTimeDiscount || 0,
                      formik.values.monthlyDiscount || 0,
                      discount,
                      formik.values,
                      newValue, // Pass the new value for trueMonthlyCost
                      formik.values.compensation,
                      formik.values.professionalServicesCost,
                      orderChannel
                    );
                    formik.setFieldValue('trueMonthlyCost', newValue);
                  },
                  readonly: false,
                  note: null,
                  disabled: isViewOnly,
                  horizontalFormat: true,
                  labelColor: '#2b8000'
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'grossMargin',
                  type: 'Currency',
                  label: 'Gross Margin',
                  value: formik.values.grossMargin,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'grossMarginPercentage',
                  type: 'Percentage',
                  label: 'Gross Margin %',
                  value: formik.values.grossMarginPercentage,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <Heading level="h3">One Time Costs</Heading>
              <FieldInput
                data={{
                  ref: null,
                  name: 'compensation',
                  type: 'Currency',
                  label: 'Compensation',
                  value: formik.values.compensation,
                  required: false,
                  onChange: (e) => {
                    const newValue = parseFloat(e.target.value) || 0;
                    setCalculationFields(
                      formik.values.oneTimeDiscount || 0,
                      formik.values.monthlyDiscount || 0,
                      discount,
                      formik.values,
                      formik.values.trueMonthlyCost,
                      newValue, // Pass the new value for compensation
                      formik.values.professionalServicesCost,
                      orderChannel
                    );
                    formik.setFieldValue('compensation', newValue);
                  },
                  readonly: false,
                  note: null,
                  disabled: isViewOnly,
                  horizontalFormat: true,
                  labelColor: '#2b8000'
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'professionalServicesOnboarding',
                  type: 'Currency',
                  label: 'Professional Services Onboarding',
                  value:
                    formik.values.professionalServicesOnboarding,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'professionalServicesCost',
                  type: 'Currency',
                  label: 'Professional Services Cost',
                  value: formik.values.professionalServicesCost,
                  required: false,
                  onChange: (e) => {
                    const newValue = parseFloat(e.target.value) || 0;
                    setCalculationFields(
                      formik.values.oneTimeDiscount || 0,
                      formik.values.monthlyDiscount || 0,
                      discount,
                      formik.values,
                      formik.values.trueMonthlyCost,
                      formik.values.compensation,
                      newValue, // Pass the new value for professionalServicesCost
                      orderChannel
                    );
                    formik.setFieldValue('professionalServicesCost', newValue);
                  },
                  readonly: false,
                  note: null,
                  disabled: isViewOnly,
                  horizontalFormat: true,
                  labelColor: '#2b8000'
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'professionalServicesMargin',
                  type: 'Currency',
                  label: 'Professional Services Margin',
                  value: formik.values.professionalServicesMargin,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
              <FieldInput
                data={{
                  ref: null,
                  name: 'professionalServicesMarginPercentage',
                  type: 'Percentage',
                  label: 'Professional Services Margin %',
                  value:
                    formik.values.professionalServicesMarginPercentage,
                  required: false,
                  onChange: null,
                  readonly: true,
                  note: null,
                  disabled: true,
                  horizontalFormat: true
                }}
              />
            </div>

            {/* Action Section */}
            <div>
              <FieldMultiLineText
                data={{
                  ref: null,
                  name: 'commentFromApprover',
                  type: 'Percentage',
                  label: 'Comment',
                  value: formik.values.commentFromApprover,
                  onChange: e => {
                    formik.setFieldValue('commentFromApprover', e.target.value);
                  },
                  required: false,
                  readonly: true,
                  note: null,
                  disabled: isViewOnly,
                  horizontalFormat: true
                }}
              />
              <FieldFile
                data={{
                  name: 'attachment',
                  label: 'Attachment',
                  value: formik.values.attachment,
                  disabled: isViewOnly,
                  onChange: (event) => {
                    const file = event.currentTarget.files[0]; // Get the selected file
                    formik.setFieldValue('attachment', file); // Update Formik state
                  }
                }}
              />
              <FieldTitle>Attached Files</FieldTitle>
              {
                discount.attachments && discount.attachments.map(({filename, id})=> {
                  const fileName = filename.substring(filename.indexOf('-') + 1);
                  return (
                    <AttachmentContainer>
                      <span>{fileName}</span>
                      <Download onClick={() => onFileDownload(fileName, id)} a11yText="Download" />
                    </AttachmentContainer>
                  )
                })
              }
              {
                (!discount.attachments || !discount.attachments.length) && <div>No files uploaded</div>
              }
              <FieldTitle style={{marginTop: 10}}>Action</FieldTitle>
              <select
                style={{ fontSize: '1rem' }}
                disabled={isViewOnly}
                value={formik.values.action}
                onChange={e => formik.setFieldValue('action', e.target.value)}
              >
                <option selected disabled value="">
                  Select An Option
                </option>
                {['Approve','Reject'].map(status => (
                  <option value={status}>{status}</option>
                ))}
              </select>

              {true && (
                <Box
                  mt={20}
                  display="flex"
                  justifyContent="center"
                  width="100"
                  style={{ alignItems: 'center' }}
                >
                  <Box>
                    <OrderButton
                      isLoading={loading}
                      label='Submit Request'
                      disabled={!formik.values.action || isViewOnly}
                      onClick={() => 
                        shouldNeedConfirmation()
                         ? setIsConfirmationModalOpen(true)
                         : formik.handleSubmit()
                      }
                      variant="purple"
                    />
                  </Box>
                </Box>
              )}
            </div>
          </form>
        </Formik>

        <div style={{ height: 30 }} />
        <PopupModal
          heading={confirmationDialog}
          bodyText={`TCV DM% is less than ${getTcvDmTermThreshold(discount.term, orderChannel)}%. Are you sure you want to proceed?`}
          confirmCTAText={confirmButtonLabel}
          cancelButtonType="button"
          cancelCTAText={cancelButtonLabel}
          onConfirm={() => formik.handleSubmit()}
          onClose={() => setIsConfirmationModalOpen(false)}
          isOpen={isConfirmationModalOpen}
        />
      </Modal>
    </div>
  );
};

DiscountCalculatorDetails.propTypes = {
  discountData: PropTypes.shape({
    orderId: PropTypes.string,
    id: PropTypes.number,
    status: PropTypes.string,
    productSelection: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onStatusUpdateSuccess: PropTypes.func.isRequired,
  isViewOnly: PropTypes.bool.isRequired,
};
export default DiscountCalculatorDetails;
